/**
 * TanStack Query hooks for invites: the public status lookup, and the admin list, create, revoke and
 * resend.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../client'
import type { components } from '../generated/schema'
import type {
  Invite,
  InviteCreate,
} from '../types'

// ── Invites (invites.manage admin) ───────────────────────────────────

type InviteStatusOut = components['schemas']['InviteStatusOut']

/**
 * Public: no credential is attached and none is needed. `retry: false` because
 * every response here is a final answer, including "unknown" -- retrying an
 * invalid code three times only spends the rate-limit budget.
 */
export function useInviteStatus(code: string) {
  return useQuery({
    queryKey: ['invite', code],
    queryFn: () => apiFetch<InviteStatusOut>(`/api/invites/${encodeURIComponent(code)}`),
    retry: false,
    enabled: Boolean(code),
  })
}

/** Every invite, newest first. Gated on `invites.manage` server-side. */
export function useInvites() {
  return useQuery({
    queryKey: ['invites'],
    queryFn: () => apiFetch<Invite[]>('/api/invites'),
  })
}

export function useCreateInvite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: InviteCreate) =>
      apiFetch<Invite>('/api/invites', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invites'] })
    },
  })
}

export function useRevokeInvite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (code: string) =>
      apiFetch<void>(`/api/invites/${encodeURIComponent(code)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invites'] })
    },
  })
}

/** IA-D14's retry. A failed send never loses the invite, so this re-attempts
 *  delivery for an existing code rather than issuing a new one. */
export function useResendInvite() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (code: string) =>
      apiFetch<Invite>(`/api/invites/${encodeURIComponent(code)}/send`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invites'] })
    },
  })
}
